// src/pages/brand-collaborations-client-stories/components/ClientTestimonials.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      title: "Chief Marketing Officer",
      company: "Global Technology Corporation",
      companyType: "Fortune 500 Technology",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "The Mechanic Photography Studio doesn\'t just deliver content; they craft strategic visual narratives that drive measurable business outcomes. Their integration with our corporate culture and ability to maintain brand consistency across multiple international projects has been exceptional.",
      metrics: {
        engagement: "450% increase",
        brandLift: "300% improvement",
        projects: "5 successful campaigns"
      },
      videoThumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=800&auto=format&fit=crop",
      rating: 5
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      title: "Development Director",
      company: "Platinum Properties International",
      companyType: "Luxury Real Estate",
      image: "https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "Their discretion and professionalism in high-stakes luxury real estate presentations is unmatched. The studio consistently captures architectural authenticity while elevating the luxury narrative. Every project demonstrates their deep understanding of our discerning clientele.",
      metrics: {
        sales: "250% increase",
        premiumPricing: "30% elevation",
        projects: "8 luxury developments"
      },
      videoThumbnail: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=800&auto=format&fit=crop",
      rating: 5
    },
    {
      id: 3,
      name: "James Thompson",
      title: "Event Director",
      company: "Elite Corporate Events",
      companyType: "Global Events Management",
      image: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "Working within corporate constraints while maintaining creative excellence is their superpower. They seamlessly integrate with our event teams, adapt to complex logistics, and deliver polished documentation that exceeds C-suite expectations consistently.",
      metrics: {
        satisfaction: "99.8% client approval",
        efficiency: "50% faster delivery",
        projects: "12 major events"
      },
      videoThumbnail: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=800&auto=format&fit=crop",
      rating: 5
    },
    {
      id: 4,
      name: "David Kim",
      title: "VP Marketing",
      company: "International Manufacturing Group",
      companyType: "Fortune 500 Manufacturing",
      image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "Strategic creative partnership that consistently delivers beyond expectations. Their ability to translate complex manufacturing processes into compelling visual stories while maintaining technical accuracy has transformed our brand perception globally.",
      metrics: {
        reach: "2M+ global impressions",
        engagement: "600% social growth",
        projects: "7 innovation showcases"
      },
      videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=800&auto=format&fit=crop",
      rating: 5
    }
  ];

  const currentTestimonial = testimonials[activeTestimonial];

  return (
    <section className="py-20 bg-background-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary-inverse mb-6">
            Executive <span className="text-gradient-gold">Testimonials</span>
          </h2>
          <p className="text-xl text-text-secondary-inverse max-w-3xl mx-auto leading-relaxed">
            C-suite executives and marketing directors share their experiences of creative quality, 
            professional excellence, and measurable business impact
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Video Testimonial Section */}
          <div className="relative">
            <div className="relative rounded-lg overflow-hidden shadow-dramatic">
              <Image
                src={currentTestimonial.videoThumbnail}
                alt={`${currentTestimonial.name} testimonial`}
                className="w-full aspect-video object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <button className="w-20 h-20 bg-accent hover:bg-accent-bright rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110">
                  <Icon name="Play" size={32} className="text-white ml-1" strokeWidth={2} />
                </button>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/60 backdrop-blur-sm rounded-lg p-4">
                  <h3 className="text-white font-bold text-lg">{currentTestimonial.name}</h3>
                  <p className="text-white/80 text-sm">{currentTestimonial.title}</p>
                  <p className="text-accent text-sm font-medium">{currentTestimonial.company}</p>
                </div>
              </div>
            </div>

            {/* Testimonial Navigation */}
            <div className="flex items-center justify-center space-x-4 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === index ? 'bg-accent' : 'bg-text-secondary-inverse/40'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Testimonial Content */}
          <div className="lg:pl-8">
            {/* Company Info */}
            <div className="mb-6">
              <div className="inline-flex items-center space-x-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-4 py-2 mb-4">
                <Icon name="Building2" size={16} color="#D4AF37" strokeWidth={2} />
                <span className="text-accent font-medium text-sm">{currentTestimonial.companyType}</span>
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center space-x-1 mb-6">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Icon key={i} name="Star" size={20} className="text-accent fill-current" strokeWidth={0} />
              ))}
              <span className="text-text-secondary-inverse ml-2">Exceptional Partnership</span>
            </div>

            {/* Testimonial Quote */}
            <blockquote className="text-xl text-text-primary-inverse leading-relaxed mb-8 italic">
              "{currentTestimonial.testimonial}"
            </blockquote>

            {/* Executive Info */}
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <Image
                  src={currentTestimonial.image}
                  alt={currentTestimonial.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h4 className="text-text-primary-inverse font-bold text-lg">{currentTestimonial.name}</h4>
                <p className="text-text-secondary-inverse">{currentTestimonial.title}</p>
                <p className="text-accent font-medium">{currentTestimonial.company}</p>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(currentTestimonial.metrics).map(([key, value], index) => (
                <div key={index} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                  <div className="text-accent font-bold text-lg mb-1">{value}</div>
                  <div className="text-text-secondary-inverse text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className="flex items-center justify-center space-x-4 mt-12">
          <button
            onClick={() => setActiveTestimonial(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
            className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-text-primary-inverse hover:bg-accent hover:text-white transition-all duration-300"
          >
            <Icon name="ChevronLeft" size={20} strokeWidth={2} />
          </button>
          <span className="text-text-secondary-inverse">
            {activeTestimonial + 1} of {testimonials.length}
          </span>
          <button
            onClick={() => setActiveTestimonial(prev => prev === testimonials.length - 1 ? 0 : prev + 1)}
            className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-text-primary-inverse hover:bg-accent hover:text-white transition-all duration-300"
          >
            <Icon name="ChevronRight" size={20} strokeWidth={2} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;